import React from "react";
import Skeleton from "react-loading-skeleton";
import Str from "../../../../common/Str";
import Badge from "../../../../library/Badge";
import Button from "../../../../library/Button";
import Checkbox from "../../../../library/Checkbox";
import DataTable, { DataTableMultiSelectFilter, DataTableStatusFilter } from "../../../../library/DataTable";
import { resolveNetsuiteProductName } from "../../../../../utils/VersionModulesUtils";
import { getRowsPerPageOptions } from "../../studyPaths/studyPaths.utils";
import { ActionsCell, PortalNameCell, PreRequisitesCell, StudyPathCell } from "./VersionModulesRowCells";
import { getVersionModulesExportOptions } from "./VersionModulesExport";
import { usePermissionGuard } from "../../../../../hooks/usePermissionGuard";

const PROCESSING_REASON = "A bulk action is currently running";
const DELETE_SCOPES = ["pmdelete"];

/**
 * Version Modules table (SPEC3 R1): the version's linked modules with study-path placement, row
 * selection for the bulk unlink bar, search/filters, exports, sorting and pagination - built on
 * the shared `DataTable`, same as the Assign Modules modal.
 * @param {object} hook State and handlers from `useVersionModules`.
 */
function VersionModulesTable({ hook }) {
    const canDelete = usePermissionGuard({ scopes: DELETE_SCOPES }) === true;

    const columns = [
        {
            key: "select",
            label: (
                <Checkbox
                    checked={hook.allMatchingSelected}
                    indeterminate={hook.someMatchingSelected}
                    disabled={hook.isProcessing || hook.loading || hook.totalRows === 0 || hook.isLocked || !canDelete}
                    onChange={hook.toggleAllMatching}
                    ariaLabel="Select all modules matching current filters"
                />
            ),
            sortable: false,
            width: "49px",
            render: (row) => (
                <Checkbox
                    checked={hook.selectedIds.includes(row.Id)}
                    disabled={hook.isProcessing || hook.isLocked || !canDelete}
                    onChange={() => hook.toggleRow(row.Id)}
                    ariaLabel={`Select ${row.Portal || "module"}`}
                />
            ),
        },
        {
            key: "Portal",
            label: "Portal Name",
            render: (row) => <PortalNameCell row={row} />,
        },
        {
            key: "Code",
            label: "Code",
            render: (row) => row.Code || "-",
        },
        {
            key: "credits",
            label: "Cr",
            render: (row) => (row.credits ? `${row.credits}cr` : "-"),
        },
        {
            key: "studyPath",
            label: "Study Path",
            render: (row) => <StudyPathCell row={row} />,
        },
        {
            key: "term",
            label: "Term",
            render: (row) => row.term ?? "-",
        },
        {
            key: "year",
            label: "Year",
            render: (row) => row.year ?? "-",
        },
        {
            key: "Programmes",
            label: "",
            icon: "books",
            ariaLabel: "Linked programmes",
            render: (row) => (
                row.Programmes ? row.Programmes : <Badge tone="warning" icon="exclamation-triangle">0</Badge>
            ),
        },
        {
            key: "netsuite_product",
            label: "NetSuite Product",
            sortable: false,
            render: (row) => (
                hook.isNetsuiteLoading ? <Skeleton width="70%" /> : resolveNetsuiteProductName(hook.netsuiteList, row.netsuite_product)
            ),
        },
        {
            key: "prerequisites",
            label: "Pre-Requisites",
            sortable: false,
            render: (row) => <PreRequisitesCell row={row} moduleNameById={hook.moduleNameById} />,
        },
        {
            key: "action",
            label: "Action",
            sortable: false,
            width: "1%",
            render: (row) => <ActionsCell row={row} hook={hook} />,
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={hook.isError ? [] : hook.rows}
            keyField="Id"
            loading={hook.loading}
            emptyMessage={hook.isError ? (
                <>
                    Modules couldn&apos;t be loaded.
                    {" "}
                    <Button type="button" variant="primary" size="sm" onClick={hook.refresh}>Retry</Button>
                </>
            ) : Str.noRecord}
            sortKey={hook.sortKey}
            sortOrder={hook.sortOrder}
            onSortChange={hook.changeSort}
            searchValue={hook.search}
            onSearchChange={(value) => {
                hook.setSearch(value);
                hook.setPage(1);
            }}
            onReset={hook.resetFilters}
            disabled={hook.isProcessing}
            disabledReason={PROCESSING_REASON}
            exportOptions={getVersionModulesExportOptions(hook)}
            filterSlot={(
                <>
                    <DataTableStatusFilter
                        value={hook.statusFilter}
                        onChange={(event) => hook.setStatusFilter(event.target.value)}
                        options={hook.statusOptions}
                        disabled={hook.isProcessing}
                        disabledReason={PROCESSING_REASON}
                    />
                    <DataTableMultiSelectFilter
                        placeholder="Term"
                        value={hook.termFilter}
                        onChange={(event) => hook.setTermFilter(event.target.value)}
                        options={hook.termOptions}
                        disabled={hook.isProcessing}
                        disabledReason={PROCESSING_REASON}
                    />
                    <DataTableMultiSelectFilter
                        placeholder="Year"
                        value={hook.yearFilter}
                        onChange={(event) => hook.setYearFilter(event.target.value)}
                        options={hook.yearOptions}
                        disabled={hook.isProcessing}
                        disabledReason={PROCESSING_REASON}
                    />
                </>
            )}
            pagination={{
                totalEntries: hook.totalRows,
                rangeStart: hook.rangeStart,
                rangeEnd: hook.rangeEnd,
                rowsPerPage: hook.perPage,
                rowsPerPageOptions: getRowsPerPageOptions(hook.totalRows),
                onRowsPerPageChange: (value) => {
                    hook.setPerPage(value);
                    hook.setPage(1);
                },
                currentPage: hook.page,
                totalPages: hook.totalPages,
                onPageChange: hook.setPage,
            }}
        />
    );
}

export default VersionModulesTable;
